import { ref } from 'vue'
import { defineStore } from 'pinia'
import api from '@/api'
import { isAiSmsPaused } from '@/lib/aiPause'
import type { AiPauseEvent, Conversation, Sms } from '@/types/types'

const popSound = new Audio('/pop.mp3')

export const useSmsStore = defineStore('sms', () => {
  const conversations = ref<Conversation[]>([])
  const conversationsLoading = ref(false)
  const conversationsPage = ref(1)
  const hasMoreConversations = ref(true)

  const currentConversation = ref<Conversation | null>(null)
  const messages = ref<Sms[]>([])
  const messagesLoading = ref(false)
  const messagesPage = ref(1)
  const hasMoreMessages = ref(true)

  // The lead's AI pause history, rendered as inline dividers in the transcript.
  const aiEvents = ref<AiPauseEvent[]>([])
  const resumingAi = ref(false)

  const totalUnreadCount = ref(0)

  const sendingMessage = ref(false)
  const tabOpen = ref(false)

  const lastViewedConversationId = ref<number | null>(null)
  const lastViewedConversationIndex = ref<number | null>(null)
  const lastScrollPosition = ref(0)

  const fetchConversations = async (page = 1) => {
    conversationsLoading.value = true
    try {
      const response = await api.get('/api/v1/conversations', {
        params: { page },
      })
      if (page === 1) {
        conversations.value = response.data.data
      } else {
        conversations.value = [...conversations.value, ...response.data.data]
      }
      conversationsPage.value = page
      hasMoreConversations.value = response.data.meta.current_page < response.data.meta.last_page
    } catch (err) {
      console.error('Failed to fetch conversations:', err)
    } finally {
      conversationsLoading.value = false
    }
  }

  const loadMoreConversations = async () => {
    if (hasMoreConversations.value && !conversationsLoading.value) {
      await fetchConversations(conversationsPage.value + 1)
    }
  }

  const fetchMessages = async (conversationId: number, page = 1) => {
    messagesLoading.value = true
    try {
      const response = await api.get(`/api/v1/conversations/${conversationId}/sms`, {
        params: { page },
      })

      // Everything below is state for the thread on screen, so a response that
      // outlived its own conversation has nothing to say. Tapping quickly
      // through the list can land an earlier fetch after a later one, and
      // without this the rep gets someone else's messages and dividers.
      if (currentConversation.value?.id !== conversationId) return

      if (page === 1) {
        messages.value = response.data.data
      } else {
        // Older messages get prepended since newest are first
        messages.value = [...response.data.data, ...messages.value]
      }
      // The pause history is lead-scoped and unpaginated, so it rides along
      // with page 1 only — later pages omit the key. Guard the assignment or
      // scrolling back through history would wipe the dividers. An empty array
      // is still truthy, so a lead with no events correctly clears them.
      if (response.data.ai_events) {
        aiEvents.value = response.data.ai_events
      }
      messagesPage.value = page
      hasMoreMessages.value = response.data.meta.current_page < response.data.meta.last_page
    } catch (err) {
      console.error('Failed to fetch messages:', err)
    } finally {
      messagesLoading.value = false
    }
  }

  const loadMoreMessages = async () => {
    if (hasMoreMessages.value && !messagesLoading.value && currentConversation.value) {
      await fetchMessages(currentConversation.value.id, messagesPage.value + 1)
    }
  }

  /**
   * Flip the AI pause on every conversation belonging to the lead.
   *
   * The switch is per lead, not per conversation: a rep who moves to the
   * lead's other number has not handed the thread back, and a lead with two
   * conversations shows the pause in both. Touching only the open thread is
   * how the list and the transcript end up disagreeing.
   */
  const setLeadAiSmsPaused = (leadId: number | undefined, paused: boolean) => {
    if (!leadId) return

    // The open thread is listed alongside the rows because it need not be one
    // of them: a conversation opened by id that missed the first page is
    // fetched on its own and never enters the list.
    for (const conversation of [currentConversation.value, ...conversations.value]) {
      if (conversation?.lead?.id === leadId) {
        conversation.lead.is_ai_sms_paused = paused
      }
    }
  }

  /**
   * Adopt a lead's pause history, but only while that lead's thread is still
   * the open one. These responses land after an await and the rep may have
   * moved on in the meantime — without the check, one lead's dividers render
   * in another lead's transcript. The pause flag itself needs no such guard:
   * `setLeadAiSmsPaused` is keyed by lead, so it stays correct either way.
   */
  const applyAiEvents = (leadId: number, events: AiPauseEvent[]) => {
    if (currentConversation.value?.lead?.id !== leadId) return

    aiEvents.value = events
  }

  /**
   * Re-read the pause history on its own, for the thread whose own send
   * triggered an automatic pause. Deliberately not the message endpoint: that
   * one paginates 30 SMS rows just to reach the same small array.
   */
  const refreshAiEvents = async (leadId?: number) => {
    const id = leadId ?? currentConversation.value?.lead?.id
    if (!id) return

    try {
      const response = await api.get(`/api/v1/leads/${id}/ai-sms-events`)
      applyAiEvents(id, response.data.ai_events ?? [])
    } catch (err) {
      console.error('Failed to fetch AI pause events:', err)
    }
  }

  const resumeAiSms = async (): Promise<{ success: boolean; error?: string }> => {
    const leadId = currentConversation.value?.lead?.id
    if (!leadId || resumingAi.value) {
      return { success: false }
    }

    resumingAi.value = true
    try {
      const response = await api.post(`/api/v1/leads/${leadId}/resume-ai-sms`)
      setLeadAiSmsPaused(leadId, !!response.data.is_ai_sms_paused)
      // The endpoint returns the refreshed history, so the resume divider
      // lands next to the pause it undoes with no second round trip.
      applyAiEvents(leadId, response.data.ai_events ?? [])
      return { success: true }
    } catch (err) {
      console.error('Failed to resume AI SMS:', err)
      return { success: false, error: 'Unable to hand this conversation back.' }
    } finally {
      resumingAi.value = false
    }
  }

  /** Everything scoped to the open thread, cleared as one so nothing lingers. */
  const resetTranscript = () => {
    messages.value = []
    messagesPage.value = 1
    hasMoreMessages.value = true
    aiEvents.value = []
  }

  const selectConversation = async (conversation: Conversation) => {
    currentConversation.value = conversation
    resetTranscript()
    await fetchMessages(conversation.id, 1)

    // Mark conversation as read
    if (conversation.unread_count > 0) {
      const unreadCount = conversation.unread_count
      const idx = conversations.value.findIndex((c) => c.id === conversation.id)
      if (idx !== -1) {
        conversations.value[idx].unread_count = 0
      }
      // Update total unread count
      totalUnreadCount.value = Math.max(0, totalUnreadCount.value - unreadCount)

      // Call API to mark as read
      try {
        await api.put(`/api/v1/conversations/${conversation.id}`)
      } catch (err) {
        console.error('Failed to mark conversation as read:', err)
      }
    }
  }

  const clearCurrentConversation = () => {
    currentConversation.value = null
    resetTranscript()
  }

  const sendMessage = async (message: string): Promise<{ success: boolean; error?: string }> => {
    if (!currentConversation.value || !message.trim()) {
      return { success: false }
    }

    // Captured before the request: the rep can open another thread while it is
    // in flight, and the pause below belongs to the lead they actually texted.
    const sentFrom = currentConversation.value

    sendingMessage.value = true
    try {
      const response = await api.post('/api/v1/sms', {
        conversation_id: sentFrom.id,
        message: message.trim(),
      })

      // API returns SMS object directly in response.data
      const newSms = response.data as Sms
      if (newSms && newSms.id) {
        // Into the transcript only if it is still the one that sent it — the
        // rep may have moved on, and the message belongs to the thread they
        // typed it in.
        if (currentConversation.value?.id === sentFrom.id) {
          messages.value.push(newSms)
        }

        // The preview goes on the row that was texted, open or not.
        const idx = conversations.value.findIndex((c) => c.id === sentFrom.id)
        if (idx !== -1) {
          conversations.value[idx].last_sms = newSms
          conversations.value[idx].last_sms_at = newSms.sent_at
        }
      }

      // The server pauses the AI on any outbound message a person wrote, so by
      // the time this resolves the lead is already paused. Mirror it locally
      // rather than waiting for the pusher broadcast to echo back, so the strip
      // and the divider are up before the rep looks away. The next sms-sent
      // event carries a full ConversationResource and overwrites this with the
      // server's own value.
      if (sentFrom.number?.has_ai_sms_agent && !isAiSmsPaused(sentFrom)) {
        setLeadAiSmsPaused(sentFrom.lead?.id, true)
        refreshAiEvents(sentFrom.lead?.id)
      }

      return { success: true }
    } catch (err: unknown) {
      console.error('Failed to send message:', err)
      if (
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'status' in err.response &&
        err.response.status === 403 &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'message' in err.response.data
      ) {
        return { success: false, error: err.response.data.message as string }
      }
      return { success: false, error: 'Failed to send message' }
    } finally {
      sendingMessage.value = false
    }
  }

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/api/v1/sms/unread')
      totalUnreadCount.value = response.data.unread_count ?? 0
    } catch (err) {
      console.error('Failed to fetch unread count:', err)
    }
  }

  /**
   * Take the fresh conversation off a broadcast: move it to the top of the
   * list, and re-point the open thread at it when it is the one on screen.
   *
   * That second part matters. `selectConversation` hands the thread the very
   * object that sits in the list, but a broadcast replaces the row wholesale —
   * so without this the two stop being the same object from the first message
   * onward, and the transcript goes on rendering whatever the conversation
   * looked like when it was opened while the list row shows the server's
   * version. The AI pause flag is the first piece of conversation state both
   * surfaces draw, so the disagreement became visible: a pause or hand-back
   * from elsewhere reached the list and never the open thread.
   */
  const adoptConversation = (conversation: Conversation) => {
    const idx = conversations.value.findIndex((c) => c.id === conversation.id)
    if (idx !== -1) {
      conversations.value.splice(idx, 1)
    }
    conversations.value.unshift(conversation)

    if (currentConversation.value?.id === conversation.id) {
      currentConversation.value = conversation
    }
  }

  const handleSmsReceived = (data: { sms: Sms; conversation: Conversation }) => {
    adoptConversation(data.conversation)

    // Refresh unread count
    fetchUnreadCount()

    // If viewing this conversation, add the message and mark as read
    if (currentConversation.value?.id === data.conversation.id) {
      messages.value.push(data.sms)

      if (tabOpen.value) {
        popSound.play().catch(() => {})
        if (/electron/i.test(navigator.userAgent)) {
          // @ts-expect-error: Only available in electron
          window.electronAPI?.incomingCall()
        }

        const convIdx = conversations.value.findIndex((c) => c.id === data.conversation.id)
        if (convIdx !== -1) {
          conversations.value[convIdx].unread_count = 0
        }

        api.put(`/api/v1/conversations/${data.conversation.id}`).catch((err) => {
          console.error('Failed to mark conversation as read:', err)
        })
      }
    } else {
      // Not viewing this conversation — play notification sound
      popSound.play().catch(() => {})
      if (/electron/i.test(navigator.userAgent)) {
        // @ts-expect-error: Only available in electron
        window.electronAPI?.incomingCall()
      }
    }
  }

  const handleSmsSent = (data: { sms: Sms; conversation: Conversation }) => {
    adoptConversation(data.conversation)

    // Refresh unread count
    fetchUnreadCount()

    // If viewing this conversation, add the message (if not already present from sendMessage)
    if (currentConversation.value?.id === data.conversation.id) {
      if (!messages.value.some((m) => m.id === data.sms.id)) {
        messages.value.push(data.sms)
      }
    }
  }

  // TODO: Remove Sms type once all users have updated to the new { sms: Sms } format
  const handleSmsDeliveryStatus = (data: Sms | { sms: Sms }) => {
    const sms = 'sms' in data ? data.sms : data
    // Update the message in the current messages list
    const idx = messages.value.findIndex((m) => m.id === sms.id)
    if (idx !== -1) {
      messages.value.splice(idx, 1, sms)
    }

    // Update last_sms in conversation if it matches
    const convIdx = conversations.value.findIndex((c) => c.id === sms.conversation_id)
    if (convIdx !== -1 && conversations.value[convIdx].last_sms?.id === sms.id) {
      conversations.value[convIdx].last_sms = sms
    }
  }

  const handleUnreadUpdated = (data?: { conversation?: Conversation }) => {
    fetchUnreadCount()

    // Update specific conversation's unread count if provided
    if (data?.conversation) {
      const idx = conversations.value.findIndex((c) => c.id === data.conversation!.id)
      if (idx !== -1) {
        conversations.value[idx].unread_count = data.conversation.unread_count
      }
    }

    // Refresh conversation list if viewing it (no current conversation selected)
    if (!currentConversation.value) {
      fetchConversations()
    }
  }

  const fetchConversation = async (conversationId: number): Promise<Conversation | null> => {
    try {
      const response = await api.get(`/api/v1/conversations/${conversationId}`)
      return response.data.data || response.data
    } catch (err) {
      console.error('Failed to fetch conversation:', err)
      return null
    }
  }

  const deleteConversation = async (conversationId: number) => {
    try {
      await api.delete(`/api/v1/conversations/${conversationId}`)

      // Remove from conversations list
      conversations.value = conversations.value.filter((c) => c.id !== conversationId)

      // Clear current conversation if it's the one being deleted
      if (currentConversation.value?.id === conversationId) {
        currentConversation.value = null
        messages.value = []
      }

      return true
    } catch (err) {
      console.error('Failed to delete conversation:', err)
      return false
    }
  }

  return {
    conversations,
    conversationsLoading,
    conversationsPage,
    hasMoreConversations,
    currentConversation,
    messages,
    messagesLoading,
    messagesPage,
    hasMoreMessages,
    aiEvents,
    resumingAi,
    totalUnreadCount,
    sendingMessage,
    tabOpen,
    refreshAiEvents,
    resumeAiSms,
    fetchConversations,
    loadMoreConversations,
    fetchMessages,
    loadMoreMessages,
    selectConversation,
    clearCurrentConversation,
    sendMessage,
    fetchUnreadCount,
    handleSmsReceived,
    handleSmsSent,
    handleSmsDeliveryStatus,
    handleUnreadUpdated,
    lastViewedConversationId,
    lastViewedConversationIndex,
    lastScrollPosition,
    fetchConversation,
    deleteConversation,
  }
})
